<template>
  <div class="buyer-list-page">
    <div class="page-header">
      <div class="header-content">
        <p class="header-eyebrow">Order Management</p>
        <h1 class="page-title">订单管理</h1>
      </div>
    </div>
    
    <!-- 统计卡片 -->
    <div v-if="!loading && buyers.length > 0" class="summary-grid">
      <div class="summary-card">
        <span class="summary-icon">📦</span>
        <span class="summary-label">全部订单</span>
        <strong class="summary-value total">{{ buyers.length }}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-icon">⏳</span>
        <span class="summary-label">待处理</span>
        <strong class="summary-value pending">{{ pendingOrderCount }}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-icon">🚚</span>
        <span class="summary-label">配送中</span>
        <strong class="summary-value shipping">{{ shippingOrderCount }}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-icon">💰</span>
        <span class="summary-label">订单总额</span>
        <strong class="summary-value amount">¥{{ formatPrice(totalOrderAmount) }}</strong>
      </div>
    </div>
    <div class="orders-container">
      <div v-if="loading" class="loading">加载中...</div>

      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      
      <div v-if="success" class="alert alert-success">
        {{ success }}
      </div>

      <!-- 订单卡片列表 -->
      <div v-if="!loading && buyers.length > 0" class="orders-list">
        <div v-for="buyer in buyers" :key="buyer.id" class="order-card">
          <!-- 订单进度时间线 -->
          <div class="order-timeline">
            <div 
              v-for="(step, index) in getOrderSteps()" 
              :key="index"
              class="timeline-step"
              :class="{
                'completed': step.status <= buyer.orderStatus,
                'current': step.status === buyer.orderStatus,
                'pending': step.status > buyer.orderStatus
              }"
            >
              <div class="step-icon">{{ step.icon }}</div>
              <div class="step-label">{{ step.label }}</div>
              <div v-if="index < getOrderSteps().length - 1" class="step-line"></div>
            </div>
          </div>
          
          <div class="order-header">
            <span class="order-id">订单号: {{ buyer.id }}</span>
            <span :class="['status-badge', getStatusClass(buyer.orderStatus)]">{{ getStatusText(buyer.orderStatus) }}</span>
          </div>
          
          <div class="order-content">
            <!-- 商品清单 -->
            <div class="products-info">
              <div class="section-header">
                <span class="section-icon">🛍️</span>
                <h4>商品清单</h4>
              </div>
              <div v-for="(item, index) in getOrderItems(buyer)" :key="index" class="product-row">
                <span class="product-name">{{ item.productName || item.name || '未知商品' }}</span>
                <span class="product-price">¥{{ formatPrice(item.price || 0) }}</span>
                <span class="product-quantity">×{{ item.quantity || 1 }}</span>
              </div>
              <div v-if="getOrderItems(buyer).length > 1" class="more-items">
                共{{ getTotalQuantity(buyer) }}件商品
              </div>
            </div>
            
            <!-- 买家信息 -->
            <div class="buyer-info">
              <div class="section-header">
                <span class="section-icon">👤</span>
                <h4>买家信息</h4>
              </div>
              <div class="info-row">
                <span class="info-icon">👤</span>
                <span class="label">购买人:</span>
                <span>{{ buyer.name }}</span>
              </div>
              <div class="info-row">
                <span class="info-icon">📞</span>
                <span class="label">电话:</span>
                <span>{{ buyer.phone }}</span>
              </div>
              <div class="info-row">
                <span class="info-icon">📍</span>
                <span class="label">地址:</span>
                <span class="address-text">{{ buyer.address }}</span>
              </div>
            </div>
            
            <!-- 订单金额 -->
            <div class="order-amount">
              <div class="section-header">
                <span class="section-icon">💰</span>
                <h4>订单金额</h4>
              </div>
              <div class="amount-row">
                <span class="label">订单总价:</span>
                <span class="total-amount">¥{{ formatPrice(getOrderTotalAmount(buyer)) }}</span>
              </div>
            </div>
          </div>
          
          <!-- 操作按钮 -->
          <div class="order-actions">
            <template v-if="buyer.orderStatus < 4">
              <button class="btn" @click="updateOrderStatus(buyer.id, 1)" :disabled="isButtonDisabled(buyer.orderStatus, 1)">商家确认</button>
              <button class="btn" @click="updateOrderStatus(buyer.id, 2)" :disabled="isButtonDisabled(buyer.orderStatus, 2)">备货完成</button>
              <button class="btn" @click="updateOrderStatus(buyer.id, 3)" :disabled="isButtonDisabled(buyer.orderStatus, 3)">开始发货</button>
              <button class="btn" @click="completeTransaction(buyer.id, true)" :disabled="isButtonDisabled(buyer.orderStatus, 4)">交易完成</button>
              <button class="btn btn-secondary" @click="completeTransaction(buyer.id, false)" :disabled="isButtonDisabled(buyer.orderStatus, 'failed')">交易失败</button>
              <button class="btn btn-danger" @click="confirmCancelOrder(buyer.id)" :disabled="isButtonDisabled(buyer.orderStatus, 'cancel')">取消订单</button>
              <button v-if="buyer.orderStatus === 2 && !deliveries[buyer.id]" class="btn btn-primary" @click="showLogisticsModal(buyer.id)">发起配送</button>
              <div v-if="deliveries[buyer.id]" class="delivery-info">
                <span>配送单号: {{ deliveries[buyer.id].deliveryCode }}</span>
                <span>物流服务商: {{ deliveries[buyer.id].logisticsProvider?.name || '未知' }}</span>
                <span :class="getDeliveryStatusClass(deliveries[buyer.id].deliveryStatus)">{{ getDeliveryStatusText(deliveries[buyer.id].deliveryStatus) }}</span>
                <button v-if="deliveries[buyer.id].deliveryStatus === '待发货'" class="btn btn-sm" @click="markAsShipped(deliveries[buyer.id].id)">标记为已发货</button>
              </div>
            </template>
            <span v-else class="no-action">已处理</span>
          </div>
        </div>
      </div>

      <div v-if="!loading && buyers.length === 0" class="alert alert-danger">
        暂无订单记录
      </div>
    </div>

    <!-- 物流服务商选择弹窗 -->
    <div v-if="isLogisticsModalVisible" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h2>选择物流服务商</h2>
          <button class="close-btn" @click="closeLogisticsModal">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="loadingProviders" class="loading">加载物流服务商...</div>
          <div v-else class="providers-list">
            <div v-for="provider in logisticsProviders" :key="provider.id" 
                 class="provider-item" 
                 :class="{ 'selected': selectedLogisticsProviderId === provider.id }"
                 @click="selectLogisticsProvider(provider.id)">
              <div class="provider-name">{{ provider.name }}</div>
              <div class="provider-code">编码: {{ provider.code }}</div>
            </div>
          </div>
          <div v-if="logisticsProviders.length === 0" class="no-providers">
            暂无可用物流服务商
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeLogisticsModal">取消</button>
          <button class="btn btn-primary" @click="confirmLogisticsProvider" :disabled="!selectedLogisticsProviderId">确认</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BuyerList',
  data() {
      return {
        buyers: [],
        loading: true,
        error: '',
        success: '',
        deliveries: {}, // 存储配送单信息，key为buyerId
        isLogisticsModalVisible: false, // 是否显示物流服务商选择弹窗
        logisticsProviders: [], // 物流服务商列表
        loadingProviders: false, // 加载物流服务商中
        selectedLogisticsProviderId: null, // 选中的物流服务商ID
        currentBuyerId: null // 当前操作的订单ID
      }
    },
  computed: {
    pendingOrderCount() {
      return this.buyers.filter(buyer => Number(buyer.orderStatus) >= 0 && Number(buyer.orderStatus) < 3).length
    },
    shippingOrderCount() {
      return this.buyers.filter(buyer => Number(buyer.orderStatus) === 3).length
    },
    totalOrderAmount() {
      return this.buyers.reduce((total, buyer) => total + this.getOrderTotalAmount(buyer), 0)
    }
  },
  created() {
    this.fetchBuyers()
    this.fetchLogisticsProviders()
  },
  methods: {
    async fetchBuyers() {
      try {
        this.loading = true
        // 从localStorage获取当前登录的卖家ID
        const sellerId = localStorage.getItem('sellerId')
        
        if (!sellerId) {
          this.error = '请先登录'
          return
        }
        
        console.log('获取订单列表，商户ID:', sellerId)
        
        // 构建请求头，添加认证信息
        const headers = {
          'X-Username': localStorage.getItem('sellerUsername')
        }
        
        // 发送API请求，带上认证信息和商户ID
        const response = await this.$axios.get(`/seller/orders/seller/${sellerId}`, {
          headers: headers
        })
        
        console.log('获取订单列表成功，返回数据:', response.data)
        
        if (response.data.success && Array.isArray(response.data.orders)) {
          this.buyers = response.data.orders
          this.error = ''
          
          // 批量加载配送单，避免逐条请求导致页面加载缓慢
          await this.fetchDeliveriesBySeller(sellerId)
        } else {
          this.buyers = []
          this.error = response.data.message || '获取订单失败'
        }
      } catch (err) {
        let errorMessage = '获取订单失败'
        if (err.response) {
          errorMessage = err.response.data?.message || `服务器错误: ${err.response.status}`
          console.error('获取订单列表 - 服务器错误响应:', {
            status: err.response.status,
            data: err.response.data
          })
        } else if (err.request) {
          errorMessage = '服务器无响应，请检查网络连接'
          console.error('获取订单列表 - 请求错误:', err.request)
        } else {
          errorMessage = `请求错误: ${err.message}`
          console.error('获取订单列表 - 请求配置错误:', err)
        }
        this.error = errorMessage
      } finally {
        this.loading = false
      }
    },
    async fetchLogisticsProviders() {
      try {
        this.loadingProviders = true
        const sellerUsername = localStorage.getItem('sellerUsername')
        const headers = {
          'X-Username': sellerUsername
        }
        
        const response = await this.$axios.get('/logistics-providers', {
          headers: headers
        })
        
        if (response.data.success) {
          this.logisticsProviders = response.data.providers
        }
      } catch (err) {
        console.error('获取物流服务商失败:', err)
      } finally {
        this.loadingProviders = false
      }
    },
    async completeTransaction(buyerId, success) {
      try {
        // 交易成功 - 更新为状态4，交易失败 - 更新为状态5
        const status = success ? 4 : 5;
        await this.updateOrderStatus(buyerId, status);
      } catch (err) {
        let errorMessage = '操作失败，请重试'
        if (err.response) {
          errorMessage = err.response.data?.message || `服务器错误: ${err.response.status}`
          console.error('完成交易 - 服务器错误响应:', {
            status: err.response.status,
            data: err.response.data
          })
        } else if (err.request) {
          errorMessage = '服务器无响应，请检查网络连接'
          console.error('完成交易 - 请求错误:', err.request)
        } else {
          errorMessage = `请求错误: ${err.message}`
          console.error('完成交易 - 请求配置错误:', err)
        }
        
        this.error = errorMessage
        // 延长错误信息显示时间，便于查看
        setTimeout(() => {
          this.error = ''
        }, 10000) // 10秒后清除错误信息
      }
    },
    async updateOrderStatus(buyerId, status) {
      try {
        // 从localStorage获取当前登录的卖家用户名
        const sellerUsername = localStorage.getItem('sellerUsername')
        
        // 构建请求头，添加认证信息
        const headers = {
          'X-Username': sellerUsername
        }
        
        console.log('发送请求:', {
          url: `/seller/orders/${buyerId}/status`,
          method: 'PUT',
          params: { status },
          headers: headers
        })
        
        await this.$axios.put(`/seller/orders/${buyerId}/status`, null, {
          params: { status },
          headers: headers
        })
        
        // 显示成功信息
        this.error = ''
        this.success = '操作成功！'
        
        // 刷新列表
        this.fetchBuyers()
        
        // 显示成功信息3秒后自动清除
        setTimeout(() => {
          this.success = ''
        }, 3000)
      } catch (err) {
        // 增强错误信息显示
        let errorMessage = '操作失败，请重试'
        if (err.response) {
          // 服务器返回了错误响应
          if (err.response.status === 400 && err.response.data?.message) {
            errorMessage = `状态更新失败: ${err.response.data.message}`
          } else {
            errorMessage = err.response.data?.message || `服务器错误: ${err.response.status}`
          }
          console.error('服务器错误响应:', {
            status: err.response.status,
            data: err.response.data,
            headers: err.response.headers
          })
        } else if (err.request) {
          // 请求已发送但没有收到响应
          errorMessage = '服务器无响应，请检查网络连接'
          console.error('请求错误:', err.request)
        } else {
          // 请求配置出错
          errorMessage = `请求错误: ${err.message}`
          console.error('请求配置错误:', err)
        }
        
        this.error = errorMessage
        // 延长错误信息显示时间，便于查看
        setTimeout(() => {
          this.error = ''
        }, 10000) // 10秒后清除错误信息
      }
    },
    getStatusText(orderStatus) {
      switch (orderStatus) {
        case 0: return '客户下单（未支付）'
        case 1: return '商家确认（已支付）'
        case 2: return '备货完成（已支付）'
        case 3: return '开始发货（已支付）'
        case 4: return '交易完成（已支付）'
        case 5: return '交易失败'
        default: return '未知状态'
      }
    },
    getStatusClass(orderStatus) {
      switch (orderStatus) {
        case 0: return 'status-ordered'
        case 1: return 'status-confirmed'
        case 2: return 'status-prepared'
        case 3: return 'status-shipping'
        case 4: return 'status-completed'
        case 5: return 'status-failed'
        default: return 'status-unknown'
      }
    },
    // 获取订单流程步骤
    getOrderSteps() {
      return [
        { status: 0, icon: '📝', label: '客户下单' },
        { status: 1, icon: '✅', label: '商家确认' },
        { status: 2, icon: '📦', label: '备货完成' },
        { status: 3, icon: '🚚', label: '开始发货' },
        { status: 4, icon: '🎉', label: '交易完成' }
      ]
    },
    // 判断按钮是否应该禁用
    isButtonDisabled(currentStatus, targetStatus) {
      // 交易失败和取消订单按钮始终可用
      if (targetStatus === 'failed' || targetStatus === 'cancel') {
        return false
      }
      // 未支付状态下（currentStatus === 0），商家无法进行任何操作
      if (currentStatus === 0) {
        return true
      }
      // 其他按钮只有当前状态与目标状态的前一个状态匹配时才可用
      return currentStatus !== (targetStatus - 1)
    },
    
    // 获取订单商品列表
    getOrderItems(order) {
      if (order.items && order.items.length > 0) {
        return order.items
      }
      // 兼容旧格式
      return [{
        productName: order.product?.name || '未知商品',
        price: order.product?.price || 0,
        quantity: order.quantity || 1
      }]
    },
    
    // 获取订单总数量
    getTotalQuantity(order) {
      if (order.items && order.items.length > 0) {
        return order.items.reduce((sum, item) => sum + (item.quantity || 1), 0)
      }
      return order.quantity || 1
    },
    
    // 获取订单总金额
    getOrderTotalAmount(order) {
      if (order.totalAmount) {
        return order.totalAmount
      }
      if (order.items && order.items.length > 0) {
        return order.items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0)
      }
      if (order.product?.price && order.quantity) {
        return order.product.price * order.quantity
      }
      return order.product?.price || 0
    },
    
    // 格式化价格
    formatPrice(price) {
      if (typeof price === 'string') {
        price = parseFloat(price)
      }
      return price.toFixed(2)
    },
    // 返回上一页
    goBack() {
      this.$router.push('/seller/dashboard')
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      // 格式化日期为更友好的格式
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    // 确认取消订单
    confirmCancelOrder(orderId) {
      if (confirm('确定要取消这个订单吗？此操作不可恢复。')) {
        this.cancelOrder(orderId)
      }
    },
    // 商家取消订单
    async cancelOrder(orderId) {
      try {
        // 从localStorage获取当前登录的卖家用户名
        const sellerUsername = localStorage.getItem('sellerUsername')
        
        // 构建请求头，添加认证信息
        const headers = {
          'X-Username': sellerUsername
        }
        
        await this.$axios.put(`/seller/orders/${orderId}/cancel`, null, {
          params: { isCustomer: false },
          headers: headers
        })
        
        // 显示成功信息
        this.error = ''
        this.success = '订单取消成功！'
        
        // 刷新列表
        this.fetchBuyers()
        
        // 显示成功信息3秒后自动清除
        setTimeout(() => {
          this.success = ''
        }, 3000)
      } catch (err) {
        // 增强错误信息显示
        let errorMessage = '取消订单失败，请重试'
        if (err.response) {
          // 服务器返回了错误响应
          errorMessage = err.response.data?.message || `服务器错误: ${err.response.status}`
          console.error('取消订单 - 服务器错误响应:', {
            status: err.response.status,
            data: err.response.data
          })
        } else if (err.request) {
          // 请求已发送但没有收到响应
          errorMessage = '服务器无响应，请检查网络连接'
          console.error('取消订单 - 请求错误:', err.request)
        } else {
          // 请求配置出错
          errorMessage = `请求错误: ${err.message}`
          console.error('取消订单 - 请求配置错误:', err)
        }
        
        this.error = errorMessage
        // 延长错误信息显示时间，便于查看
        setTimeout(() => {
          this.error = ''
        }, 10000) // 10秒后清除错误信息
      }
    },
    // 获取配送单信息
    async fetchDeliveriesBySeller(sellerId) {
      try {
        const sellerUsername = localStorage.getItem('sellerUsername')
        const headers = {
          'X-Username': sellerUsername
        }
        
        const response = await this.$axios.get(`/deliveries/seller/${sellerId}`, {
          headers: headers
        })
        
        if (response.data.success && Array.isArray(response.data.deliveries)) {
          const deliveryMap = {}
          for (const delivery of response.data.deliveries) {
            const orderId = delivery?.order?.id
            if (orderId) {
              deliveryMap[orderId] = delivery
            }
          }
          this.deliveries = deliveryMap
        }
      } catch (err) {
        console.error('批量获取配送单信息失败:', err)
      }
    },
    // 显示物流服务商选择弹窗
    showLogisticsModal(buyerId) {
      this.currentBuyerId = buyerId
      this.selectedLogisticsProviderId = null
      this.isLogisticsModalVisible = true
    },
    // 关闭物流服务商选择弹窗
    closeLogisticsModal() {
      this.isLogisticsModalVisible = false
      this.currentBuyerId = null
      this.selectedLogisticsProviderId = null
    },
    // 选择物流服务商
    selectLogisticsProvider(providerId) {
      this.selectedLogisticsProviderId = providerId
    },
    // 确认选择物流服务商并创建配送单
    async confirmLogisticsProvider() {
      if (!this.selectedLogisticsProviderId) {
        return
      }

      try {
        const sellerUsername = localStorage.getItem('sellerUsername')
        const headers = {
          'X-Username': sellerUsername
        }

        const sellerId = localStorage.getItem('sellerId')
        const response = await this.$axios.post('/deliveries/create', {
          orderId: this.currentBuyerId,
          logisticsProviderId: this.selectedLogisticsProviderId,
          sellerId: sellerId ? parseInt(sellerId) : null
        }, {
          headers: headers
        })

        if (response.data.success) {
          this.success = '配送单创建成功！'
          // 关闭弹窗
          this.closeLogisticsModal()
          const sellerId = localStorage.getItem('sellerId')
          if (sellerId) {
            await this.fetchDeliveriesBySeller(sellerId)
          }
          // 刷新列表
          await this.fetchBuyers()
          setTimeout(() => {
            this.success = ''
          }, 3000)
        }
      } catch (err) {
        this.error = err.response?.data?.message || '创建配送单失败'
        setTimeout(() => {
          this.error = ''
        }, 10000)
      }
    },
    // 标记为已发货
    async markAsShipped(deliveryId) {
      try {
        const sellerUsername = localStorage.getItem('sellerUsername')
        const headers = {
          'X-Username': sellerUsername
        }
        
        const response = await this.$axios.put(`/deliveries/${deliveryId}/ship`, null, {
          headers: headers
        })
        
        if (response.data.success) {
          this.success = '已标记为发货！'
          // 刷新列表
          await this.fetchBuyers()
          setTimeout(() => {
            this.success = ''
          }, 3000)
        }
      } catch (err) {
        this.error = err.response?.data?.message || '标记发货失败'
        setTimeout(() => {
          this.error = ''
        }, 10000)
      }
    },
    // 获取配送状态文本
    getDeliveryStatusText(status) {
      switch (status) {
        case '待发货': return '待发货'
        case '已发货': return '已发货'
        case '已送达': return '已送达'
        case '取消': return '已取消'
        default: return '未知状态'
      }
    },
    // 获取配送状态样式
    getDeliveryStatusClass(status) {
      switch (status) {
        case '待发货': return 'delivery-pending'
        case '已发货': return 'delivery-shipped'
        case '已送达': return 'delivery-delivered'
        case '取消': return 'delivery-cancelled'
        default: return 'delivery-unknown'
      }
    }
  }
}
</script>

<style scoped>
/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.header-eyebrow {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.page-title {
  margin: 0;
  font-size: 2rem;
  color: #0f172a;
  letter-spacing: -0.03em;
}

.loading {
  text-align: center;
  padding: 3rem;
  color: #64748b;
}

/* 页面容器 */
.buyer-list-page {
  padding: 0 1rem;
}

/* 统计卡片 */
.summary-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.summary-icon {
  font-size: 1.4rem;
}

.summary-label {
  flex: 1;
  color: var(--color-text-soft);
}

.summary-value {
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.03em;
}

.summary-value.total {
  color: #0f172a;
}

.summary-value.pending {
  color: #f59e0b;
}

.summary-value.shipping {
  color: #3b82f6;
}

.summary-value.amount {
  color: #10b981;
}

/* 订单时间线 */
.order-timeline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.timeline-step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 0.8rem;
  background: rgba(15, 23, 42, 0.06);
  transition: all 0.2s ease;
}

.timeline-step.completed .step-icon {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.timeline-step.current .step-icon {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
}

.timeline-step.pending .step-icon {
  background: rgba(15, 23, 42, 0.06);
  color: #9ca3af;
}

.step-label {
  font-size: 0.82rem;
  font-weight: 500;
  color: #64748b;
}

.timeline-step.completed .step-label {
  color: #10b981;
}

.timeline-step.current .step-label {
  color: #3b82f6;
}

.step-line {
  flex: 1;
  height: 2px;
  margin: 0 0.5rem;
  background: rgba(15, 23, 42, 0.1);
}

.timeline-step.completed .step-line {
  background: rgba(16, 185, 129, 0.3);
}

.timeline-step.current .step-line {
  background: rgba(59, 130, 246, 0.3);
}

/* 状态徽章 */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-ordered {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

.status-confirmed {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.status-prepared {
  background: rgba(168, 85, 247, 0.12);
  color: #9333ea;
}

.status-shipping {
  background: rgba(236, 72, 153, 0.12);
  color: #db2777;
}

.status-completed {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.status-failed {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}

.status-unknown {
  background: rgba(15, 23, 42, 0.06);
  color: #64748b;
}

/* 区块头部 */
.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.8rem;
}

.section-icon {
  font-size: 0.95rem;
}

.section-header h4 {
  margin: 0;
  font-size: 0.95rem;
  color: #0f172a;
}

/* 买家信息卡片 */
.buyer-info {
  padding: 0.85rem;
  background: rgba(15, 23, 42, 0.03);
  border-radius: 12px;
}

.buyer-info .section-header {
  margin-bottom: 0.5rem;
}

/* 信息行 */
.info-row {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-bottom: 0.5rem;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-icon {
  display: none;
}

.info-row .label {
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.info-row span:last-child {
  font-size: 0.92rem;
  color: #0f172a;
  line-height: 1.5;
}

.info-row .address-text {
  white-space: pre-wrap;
  word-break: break-word;
}

/* 金额行 */
.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* 表格容器，支持横向滚动 */
.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1200px; /* 最小宽度确保表格不会太挤 */
  border-collapse: collapse;
  margin-top: 1rem;
  font-size: 0.9rem;
}

th, td {
  border: 1px solid #ddd;
  padding: 0.8rem 0.5rem;
  text-align: left;
  white-space: nowrap; /* 防止文字换行 */
}

/* 调整各列宽度 */
th:nth-child(1), td:nth-child(1) { width: 50px; } /* ID */
th:nth-child(2), td:nth-child(2) { width: 120px; } /* 商品 */
th:nth-child(3), td:nth-child(3) { width: 100px; } /* 单价 */
th:nth-child(4), td:nth-child(4) { width: 60px; text-align: center; } /* 数量 */
th:nth-child(5), td:nth-child(5) { width: 100px; } /* 总价 */
th:nth-child(6), td:nth-child(6) { width: 80px; } /* 购买人 */
th:nth-child(7), td:nth-child(7) { width: 110px; } /* 电话 */
th:nth-child(8), td:nth-child(8) { width: 150px; } /* 地址 */
th:nth-child(9), td:nth-child(9) { width: 130px; } /* 时间 */
th:nth-child(10), td:nth-child(10) { width: 100px; text-align: center; } /* 状态 */
th:nth-child(11), td:nth-child(11) { width: 200px; } /* 操作 */

th {
  background-color: #f8f9fa;
  font-weight: bold;
  font-size: 0.85rem;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.btn {
  padding: 0.25rem 0.5rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 0.3rem;
  margin-bottom: 0.3rem;
  display: inline-block;
  font-size: 0.8rem;
}

.btn:hover {
  background: #218838;
}

/* 禁用按钮样式 */
.btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn:disabled:hover {
  background: #ccc;
}

.btn-danger {
  background: #dc3545;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-danger:disabled {
  background: #ccc;
}

.btn-secondary:disabled {
  background: #ccc;
}

.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

h1 {
  margin-bottom: 0;
}

.btn-secondary {
  background: #6c757d;
}

.btn-secondary:hover {
  background: #545b62;
}

.status-ordered {
  color: #ffc107;
  font-weight: bold;
}

.status-confirmed {
  color: #17a2b8;
  font-weight: bold;
}

.status-prepared {
  color: #007bff;
  font-weight: bold;
}

.status-shipping {
  color: #6f42c1;
  font-weight: bold;
}

.status-completed {
  color: #28a745;
  font-weight: bold;
}

.status-failed {
  color: #dc3545;
  font-weight: bold;
}

.status-unknown {
  color: #6c757d;
  font-weight: bold;
}

.delivery-info {
  margin-top: 0.8rem;
  padding: 0.5rem;
  background-color: #e7f3ff;
  border-radius: 4px;
  border: 1px solid #b3d7ff;
}

.delivery-info span {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.9rem;
}

.delivery-pending {
  color: #ffc107;
  font-weight: bold;
}

.delivery-shipped {
  color: #28a745;
  font-weight: bold;
}

.delivery-delivered {
  color: #17a2b8;
  font-weight: bold;
}

.delivery-cancelled {
  color: #dc3545;
  font-weight: bold;
}

.delivery-unknown {
  color: #6c757d;
  font-weight: bold;
}

.alert {
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.alert-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

/* 物流服务商选择弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 1rem;
}

.providers-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.provider-item {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s;
}

.provider-item:hover {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.provider-item.selected {
  border-color: #007bff;
  background-color: #e7f3ff;
  box-shadow: 0 0 4px rgba(0, 123, 255, 0.3);
}

.provider-name {
  font-weight: bold;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.provider-code {
  font-size: 0.85rem;
  color: #666;
}

.no-providers {
  text-align: center;
  padding: 2rem;
  color: #999;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #eee;
}

.btn-sm {
  padding: 0.2rem 0.4rem;
  font-size: 0.85rem;
}

.btn-primary {
  background: #007bff;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn:disabled:hover {
  background: #ccc;
}

.btn-danger {
  background: #dc3545;
  margin-top: 0.8rem;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-danger:disabled {
  background: #ccc;
}

.btn-secondary:disabled {
  background: #ccc;
}

.buyer-list-page {
  position: relative;
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.header-container h1 {
  margin: 0;
  color: var(--color-text);
  font-size: clamp(2rem, 3vw, 2.8rem);
  letter-spacing: -0.05em;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.15rem;
}

.summary-card {
  display: grid;
  gap: 0.45rem;
  padding: 1.15rem 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: var(--shadow-soft);
}

.summary-label {
  color: var(--color-text-soft);
}

.summary-value {
  color: var(--color-text);
  font-size: 1.35rem;
  letter-spacing: -0.03em;
}

.orders-container {
  display: grid;
  gap: 1rem;
}

.orders-list {
  display: grid;
  gap: 1.15rem;
}

.order-card {
  display: grid;
  gap: 1.2rem;
  overflow: hidden;
  padding: 1.3rem;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: var(--shadow-soft);
  transition:
    transform var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
}

.order-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-medium);
}

.order-content {
  display: grid;
  grid-template-columns: minmax(280px, 1.65fr) minmax(240px, 1.1fr) minmax(180px, 0.9fr);
  gap: 1rem;
  align-items: start;
}

.order-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
}

.order-id {
  color: var(--color-text);
  font-size: 1.05rem;
  font-weight: 700;
}

.order-time {
  color: var(--color-text-soft);
}

.products-info h4 {
  margin: 0 0 0.8rem;
  color: var(--color-text);
  font-size: 0.98rem;
}

.product-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.7rem 0;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.product-row:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.product-name,
.total-amount {
  color: var(--color-text);
  font-weight: 600;
}

.products-info,
.buyer-info,
.order-amount,
.order-status-section {
  min-width: 0;
}

.product-name,
.address-text,
.info-row span:last-child {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.product-name {
  line-height: 1.45;
}

.product-price,
.product-quantity,
.label,
.more-items,
.address-text {
  color: var(--color-text-soft);
}

.info-row {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 0.7rem;
  margin-bottom: 0.65rem;
}

.info-row:last-child {
  margin-bottom: 0;
}

.order-status-section {
  display: flex;
  grid-column: 1 / -1;
  align-items: flex-start;
  justify-content: flex-end;
  padding-top: 0.2rem;
}

.order-status-section > span {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 1rem;
  border-radius: 999px;
  font-weight: 600;
  max-width: 100%;
  white-space: normal;
}

.products-info,
.buyer-info,
.order-amount,
.delivery-info {
  border-radius: 20px;
  min-height: 100%;
  padding: 1rem 1.1rem;
  background: rgba(15, 23, 42, 0.03);
}

.order-amount {
  display: grid;
  gap: 0.5rem;
  align-content: start;
}

.products-info {
  align-content: start;
}

.order-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  padding-top: 0.35rem;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.order-actions .btn {
  flex: 0 0 auto;
  min-height: 42px;
  padding-inline: 1rem;
  border-radius: 999px;
  border: 1px solid transparent;
  background: rgba(15, 23, 42, 0.05);
  color: var(--color-text);
}

.order-actions .btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
}

.order-actions .btn.btn-primary,
.order-actions .btn:not(.btn-secondary):not(.btn-danger) {
  background: linear-gradient(135deg, #111113, #2c2c2e);
  color: #fff;
}

.order-actions .btn.btn-secondary {
  background: rgba(148, 163, 184, 0.14);
  border-color: rgba(148, 163, 184, 0.3);
}

.order-actions .btn.btn-danger {
  background: rgba(185, 28, 28, 0.08);
  color: #991b1b;
  border-color: rgba(185, 28, 28, 0.16);
}

.delivery-info {
  width: 100%;
}

.provider-item {
  border-radius: 20px;
}

@media (max-width: 1380px) and (min-width: 1201px) {
  .order-content {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }
}

@media (max-width: 1200px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .order-content {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 760px) {
  .summary-grid,
  .order-content,
  .product-row,
  .info-row {
    grid-template-columns: 1fr;
  }
}
</style>